export interface FooterLink {
  label: string
  to: string
}

export function parseFooterLinks(raw?: string | null, fallback: FooterLink[] = []): FooterLink[] {
  if (!raw) return fallback
  try {
    const parsed = JSON.parse(raw) as FooterLink[]
    if (!Array.isArray(parsed)) return fallback
    return parsed.filter((item) => item.label && item.to)
  } catch {
    return fallback
  }
}

function clamp(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)))
}

function parseHex(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '').trim()
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16)
    const g = parseInt(clean[1] + clean[1], 16)
    const b = parseInt(clean[2] + clean[2], 16)
    return Number.isNaN(r) ? null : [r, g, b]
  }
  if (clean.length !== 6) return null
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return Number.isNaN(r) ? null : [r, g, b]
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

function mix(
  base: [number, number, number],
  target: [number, number, number],
  weight: number,
): string {
  const r = clamp(base[0] * (1 - weight) + target[0] * weight)
  const g = clamp(base[1] * (1 - weight) + target[1] * weight)
  const b = clamp(base[2] * (1 - weight) + target[2] * weight)
  return rgbToHex(r, g, b)
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const toLinear = (channel: number) => {
    const s = channel / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/** White or black text for contrast on a solid primary background. */
export function contrastTextOnColor(hex: string): '#ffffff' | '#000000' {
  const rgb = parseHex(hex) ?? [37, 99, 235]
  return relativeLuminance(rgb) > 0.45 ? '#000000' : '#ffffff'
}

export function generateThemeCss(primaryColor: string): string {
  const base = parseHex(primaryColor) ?? [37, 99, 235]
  const white: [number, number, number] = [255, 255, 255]
  const black: [number, number, number] = [0, 0, 0]
  const hex = rgbToHex(...base)
  const onPrimary = contrastTextOnColor(hex)

  const shades: Record<string, string> = {
    50: mix(base, white, 0.92),
    100: mix(base, white, 0.84),
    200: mix(base, white, 0.68),
    300: mix(base, white, 0.52),
    400: mix(base, white, 0.28),
    500: mix(base, white, 0.12),
    600: hex,
    700: mix(base, black, 0.14),
    800: mix(base, black, 0.28),
    900: mix(base, black, 0.42),
    950: mix(base, black, 0.56),
  }

  const vars = Object.entries(shades)
    .flatMap(([shade, color]) => [
      `--color-brand-${shade}:${color}`,
      `--ui-color-primary-${shade}:${color}`,
    ])
    .join(';')

  return `:root{${vars};--ui-text-on-primary:${onPrimary}}`
}

export function applyThemeCss(primaryColor: string): void {
  if (!import.meta.client) return
  const styleId = 'site-theme-vars'
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null
  if (!styleEl) {
    styleEl = document.createElement('style')
    styleEl.id = styleId
    document.head.appendChild(styleEl)
  }
  styleEl.innerHTML = generateThemeCss(primaryColor)
}
